import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Home as HomeIcon,
  ReceiptLong as ReceiptLongIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import paymentService from "../services/paymentService";

// Colores corporativos
const colors = {
  primary: "#00E400",
  primaryHover: "#00CC00",
  secondary: "#000000",
  background: "#F5F5F5",
  white: "#FFFFFF",
  textDark: "#000000",
  textLight: "#666666",
  success: "#00E400",
  warning: "#FFC107",
  error: "#DC3545",
};

// Función para obtener el ID de transacción desde diferentes estructuras
const getTransactionId = (transaction) => {
  // Primero buscar en more.payment.id
  if (transaction?.more?.payment?.id) {
    return transaction.more.payment.id;
  }
  // Buscar en more.payment.data[0].payment_id
  if (transaction?.more?.payment?.data?.[0]?.payment_id) {
    return transaction.more.payment.data[0].payment_id;
  }
  // Buscar en more.link.data.payment_id
  if (transaction?.more?.link?.data?.payment_id) {
    return transaction.more.link.data.payment_id;
  }
  // Buscar en metadata.id
  if (transaction?.metadata?.id) {
    return transaction.metadata.id;
  }
  return null;
};

// Función para obtener el estado del pago
const getPaymentStatus = (transaction) => {
  // Primero buscar en more.payment.status
  if (transaction?.more?.payment?.status) {
    return transaction.more.payment.status;
  }
  // Buscar en more.payment.data[0].status
  if (transaction?.more?.payment?.data?.[0]?.status) {
    return transaction.more.payment.data[0].status;
  }
  // Buscar en more.link.data.status
  if (transaction?.more?.link?.data?.status) {
    return transaction.more.link.data.status;
  }
  return "COMPLETED";
};

const PagoExitoso = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { id } = useParams();
  const [pagoInfo, setPagoInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusSent, setStatusSent] = useState(false);

  // Función para convertir imagen a Base64
  const getLogoBase64 = async () => {
    try {
      const response = await fetch("/img/logohorinzontal.png");
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Error cargando logo:", error);
      return null;
    }
  };

  const updateFacturaStatus = (facturaId, newStatus, paymentInfo = {}) => {
    try {
      const saved = localStorage.getItem("facturasGoPass");
      if (saved) {
        const facturas = JSON.parse(saved);
        const facturaIndex = facturas.findIndex((f) => f.id === facturaId);
        if (facturaIndex !== -1) {
          facturas[facturaIndex] = {
            ...facturas[facturaIndex],
            estado: newStatus,
            paymentCompleted: true,
            paymentCompletedAt: new Date().toISOString(),
            ...paymentInfo,
          };
          localStorage.setItem("facturasGoPass", JSON.stringify(facturas));
        }
      }
    } catch (error) {
      console.error("Error actualizando estado de factura:", error);
    }
  };

  const getStatusMessage = (status) => {
    const messages = {
      AUTHORIZED: "Autorizado",
      APPROVED: "Aprobado",
      COMPLETED: "Completado",
      PENDING: "Pendiente",
      SUCCESS: "Exitoso",
      CAPTURED: "Capturado",
    };
    return messages[status] || status || "Completado";
  };

  useEffect(() => {
    const loadTransactionData = async () => {
      setLoading(true);
      setError(null);
      try {
        if (id && !statusSent) {
          await paymentService.acceptPayment(id);
          setStatusSent(true);
        }

        const response = await paymentService.getTransactionById(id);
        let monto = 0;
        let empresa = "No disponible";
        let nit = "No disponible";
        let facturaNumero = "No disponible";
        let transactionId = id;
        let paymentStatus = "completado";
        let fecha = new Date().toLocaleString("es-CO");

        if (response && response.code === 0 && response.data) {
          const transaction = response.data;

          if (transaction.bill_number) facturaNumero = transaction.bill_number;
          if (transaction.company) empresa = transaction.company;
          if (transaction.nit) nit = transaction.nit;
          if (transaction.createdAt)
            fecha = new Date(transaction.createdAt).toLocaleString("es-CO");

          if (transaction.metadata) {
            const metadata = transaction.metadata;
            if (metadata.data?.amount?.value) {
              monto = parseFloat(metadata.data.amount.value);
            }
            if (metadata.data?.customer) {
              const customer = metadata.data.customer;
              if (customer.name && customer.name !== "not-fount")
                empresa = customer.name;
              if (customer.nit) nit = customer.nit;
              if (customer.factura && customer.factura !== "")
                facturaNumero = customer.factura;
            }
          }

          if (transaction.more) {
            const more = transaction.more;

            if (more.link?.data) {
              const linkData = more.link.data;
              if (monto === 0 && linkData.amount?.value) {
                monto = parseFloat(linkData.amount.value);
              }
              if (linkData.customer) {
                if (
                  empresa === "No disponible" &&
                  linkData.customer.name &&
                  linkData.customer.name !== "not-fount"
                ) {
                  empresa = linkData.customer.name;
                }
                if (nit === "No disponible" && linkData.customer.nit) {
                  nit = linkData.customer.nit;
                }
                if (
                  facturaNumero === "No disponible" &&
                  linkData.customer.factura &&
                  linkData.customer.factura !== ""
                ) {
                  facturaNumero = linkData.customer.factura;
                }
              }
            }

            const foundTransactionId = getTransactionId(transaction);
            if (foundTransactionId) {
              transactionId = foundTransactionId;
            }

            const foundStatus = getPaymentStatus(transaction);
            if (foundStatus) {
              paymentStatus = foundStatus;
            }

            if (monto === 0 && more.payment?.current_amount) {
              monto = more.payment.current_amount;
            }
            if (monto === 0 && more.payment?.transactions?.[0]?.amount) {
              monto = more.payment.transactions[0].amount;
            }
          }

          const saved = localStorage.getItem("facturasGoPass");
          if (saved) {
            const facturas = JSON.parse(saved);
            const facturaEncontrada = facturas.find(
              (f) =>
                f.numeroFactura === facturaNumero ||
                f.paymentId === transactionId ||
                f.id.toString() === id,
            );
            if (facturaEncontrada) {
              updateFacturaStatus(facturaEncontrada.id, "pagado", {
                paymentId: transactionId,
                paymentCompletedAt: new Date().toISOString(),
                montoPagado: monto,
              });
            }
          }
        }

        setPagoInfo({
          monto,
          facturaNumero,
          empresa,
          nit,
          fecha,
          transactionId,
          status: paymentStatus,
          statusMessage: getStatusMessage(paymentStatus),
        });
      } catch (err) {
        console.error("Error:", err);
        setError("Error al cargar los datos de la transacción");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadTransactionData();
    } else {
      setError("No se encontró ID de transacción");
      setLoading(false);
    }
  }, [id, statusSent]);

  const handleVolver = () => {
    navigate("/");
  };

  const handleDescargarPDF = async () => {
    const doc = new jsPDF();
    const logoBase64 = await getLogoBase64();

    doc.setFont("helvetica");

    // Logo centrado en el PDF
    if (logoBase64) {
      // Calcular posición centrada: (ancho página - ancho logo) / 2
      // Ancho página = 210mm, logo aproximadamente 50mm de ancho
      const pageWidth = 210;
      const logoWidth = 50;
      const xPos = (pageWidth - logoWidth) / 2;
      doc.addImage(logoBase64, "PNG", xPos, 10, logoWidth, 15);
    } else {
      doc.setFontSize(18);
      doc.setTextColor(0, 228, 0);
      doc.text("goPass", 105, 20, { align: "center" });
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text("Portal de Recaudos", 105, 28, { align: "center" });
    }

    // Título
    doc.setFontSize(20);
    doc.setTextColor(0, 0, 0);
    doc.text("COMPROBANTE DE PAGO", 105, 45, { align: "center" });

    // Línea decorativa
    doc.setDrawColor(0, 228, 0);
    doc.setLineWidth(0.5);
    doc.line(15, 52, 195, 52);

    // Fecha de generación
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generado: ${new Date().toLocaleString("es-CO")}`, 15, 62);

    // Información de la transacción
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("Detalles de la Transacción", 15, 79);

    // Crear tabla usando autoTable
    const tableData = [
      ["Número de Factura", pagoInfo?.facturaNumero || "No disponible"],
      ["Empresa / Usuario", pagoInfo?.empresa || "No disponible"],
      ["NIT / Identificación", pagoInfo?.nit || "No disponible"],
      ["Fecha y Hora", pagoInfo?.fecha || new Date().toLocaleString("es-CO")],
      ["ID de Transacción", pagoInfo?.transactionId || "No disponible"],
      ["Estado", getStatusMessage(pagoInfo?.status)],
    ];

    autoTable(doc, {
      startY: 86,
      head: [["Campo", "Valor"]],
      body: tableData,
      theme: "striped",
      headStyles: {
        fillColor: [0, 0, 0],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      styles: { fontSize: 10, cellPadding: 5 },
      columnStyles: {
        0: { fontStyle: "bold", textColor: [0, 0, 0] },
        1: { textColor: [0, 0, 0] },
      },
    });

    // Total
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text("Total Pagado:", 120, finalY);
    doc.setFontSize(16);
    doc.setTextColor(0, 228, 0);
    doc.text(
      `$${(pagoInfo?.monto || 0).toLocaleString("es-CO")} COP`,
      120,
      finalY + 8,
    );

    // Línea final
    doc.setDrawColor(0, 228, 0);
    doc.line(15, finalY + 20, 195, finalY + 20);

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(
      "Pagos Automaticos de Colombia - Portal de Recaudos",
      105,
      finalY + 35,
      { align: "center" },
    );
    doc.text(
      "Documento de valor legal - Este comprobante es generado automáticamente.",
      105,
      finalY + 42,
      { align: "center" },
    );

    doc.save(`comprobante_pago_${pagoInfo?.transactionId || "factura"}.pdf`);
  };

  const handleImprimir = () => {
    window.print();
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: colors.background,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <CircularProgress sx={{ color: colors.primary }} />
        <Typography sx={{ color: colors.textLight }}>
          Cargando información de la transacción...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: colors.background,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: colors.background,
        py: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Container maxWidth="md">
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            background: colors.white,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: { xs: 3, sm: 4 },
              textAlign: "center",
              background: colors.white,
              borderBottom: `3px solid ${colors.primary}`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* Logo centrado */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                mb: 2,
              }}
            >
              <Box
                component="img"
                src="/img/logohorinzontal.png"
                alt="GoPass"
                sx={{
                  height: { xs: 45, sm: 65 },
                  width: "auto",
                  objectFit: "contain",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: { xs: 90, sm: 110 },
                height: { xs: 90, sm: 110 },
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryHover} 100%)`,
                margin: "0 auto",
                mb: 2,
                animation: "pulse 0.5s ease-in-out",
                "@keyframes pulse": {
                  "0%": { transform: "scale(0.8)", opacity: 0 },
                  "100%": { transform: "scale(1)", opacity: 1 },
                },
              }}
            >
              <CheckCircleIcon
                sx={{ fontSize: { xs: 55, sm: 65 }, color: colors.secondary }}
              />
            </Box>

            <Typography
              variant={isMobile ? "h5" : "h4"}
              gutterBottom
              sx={{ color: colors.primary, fontWeight: "bold" }}
            >
              ¡Pago Completado Exitosamente!
            </Typography>
            <Typography variant="body1" sx={{ color: colors.textLight }}>
              Su transacción ha sido procesada correctamente
            </Typography>
          </Box>

          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Typography
              variant="h6"
              sx={{ color: colors.secondary, mb: 2, fontWeight: "bold" }}
            >
              Detalles de la Transacción
            </Typography>

            <Paper
              sx={{
                p: { xs: 2, sm: 3 },
                bgcolor: colors.background,
                borderRadius: 2,
                mb: 3,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.textLight }}
                  >
                    Número de Factura
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: colors.secondary,
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <ReceiptLongIcon
                      sx={{ mr: 1, fontSize: 18, color: colors.primary }}
                    />
                    {pagoInfo?.facturaNumero || "No disponible"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.textLight }}
                  >
                    Monto Pagado
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ color: colors.primary, fontWeight: "bold" }}
                  >
                    ${(pagoInfo?.monto || 0).toLocaleString("es-CO")} COP
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.textLight }}
                  >
                    Empresa/Usuario
                  </Typography>
                  <Typography variant="body1" sx={{ color: colors.secondary }}>
                    {pagoInfo?.empresa || "No disponible"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.textLight }}
                  >
                    NIT / Identificación
                  </Typography>
                  <Typography variant="body1" sx={{ color: colors.secondary }}>
                    {pagoInfo?.nit || "No disponible"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.textLight }}
                  >
                    Fecha y Hora
                  </Typography>
                  <Typography variant="body1" sx={{ color: colors.secondary }}>
                    {pagoInfo?.fecha || new Date().toLocaleString("es-CO")}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.textLight }}
                  >
                    ID de Transacción
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.textLight,
                      fontFamily: "monospace",
                      wordBreak: "break-all",
                    }}
                  >
                    {pagoInfo?.transactionId || id || "No disponible"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.textLight }}
                  >
                    Estado
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: colors.primary, fontWeight: "bold" }}
                  >
                    ✅{" "}
                    {pagoInfo?.statusMessage ||
                      pagoInfo?.status ||
                      "Completado"}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <Divider sx={{ my: 3, borderColor: colors.primary }} />

            <Alert
              severity="success"
              sx={{
                mb: 3,
                bgcolor: "#d4edda",
                border: "1px solid #c3e6cb",
                "& .MuiAlert-icon": { color: colors.primary },
              }}
            >
              <Typography variant="body2" sx={{ color: "#155724" }}>
                Puede descargar o imprimir este comprobante para sus registros.
              </Typography>
            </Alert>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDescargarPDF}
                sx={{
                  borderColor: colors.primary,
                  color: colors.primary,
                  fontWeight: "bold",
                  "&:hover": {
                    borderColor: colors.primaryHover,
                    backgroundColor: `rgba(0, 228, 0, 0.1)`,
                  },
                }}
              >
                Descargar PDF
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handleImprimir}
                sx={{
                  borderColor: colors.primary,
                  color: colors.primary,
                  fontWeight: "bold",
                  "&:hover": {
                    borderColor: colors.primaryHover,
                    backgroundColor: `rgba(0, 228, 0, 0.1)`,
                  },
                }}
              >
                Imprimir
              </Button>
              <Button
                variant="contained"
                startIcon={<HomeIcon />}
                onClick={handleVolver}
                sx={{
                  bgcolor: colors.primary,
                  textTransform: "none",
                  color: colors.secondary,
                  fontWeight: "bold",
                  "&:hover": { bgcolor: colors.primaryHover },
                }}
              >
                Volver al Inicio
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Typography variant="caption" sx={{ color: colors.textLight }}>
            Pagos Automaticos de Colombia - Portal de Recaudos | Pago procesado
            de forma segura
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PagoExitoso;
